import _extends from "@babel/runtime/helpers/extends";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
var _excluded = ["checked", "indeterminate", "onChange", "onClick", "size", "tabIndex", "className", "style", "breakpoints"];
import "core-js/modules/es.array.includes.js";
import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import styled, { css } from 'styled-components';
import { pxToEm, pxToRem, alpha, contrast, getDimmedTextColor, createStyleFunction } from 'utils/ui';
import { fillParent } from 'styles/mixins';
import useMergedRefs from 'hooks/useMergedRefs';
import Icon from 'components/Icon';
import { useTabIndexContext } from 'components/TabIndexContext';
var SIZE_MAP = {
  small: 18,
  default: 21
};
// eslint-disable-next-line @typescript-eslint/no-empty-function
var noop = function noop() {};
var Wrapper = styled.span.withConfig({
  displayName: "Checkbox__Wrapper",
  componentId: "sc-rxonnb-0"
})(["position:relative;display:inline-flex;"]);
var EXTEND_TAP_AREA_SPACING = pxToRem(6);
var Input = styled.input.withConfig({
  displayName: "Checkbox__Input",
  componentId: "sc-rxonnb-1"
})(["position:absolute;top:-", ";left:-", ";width:calc(100% + ", " * 2);height:calc(100% + ", " * 2);opacity:0;", ""], EXTEND_TAP_AREA_SPACING, EXTEND_TAP_AREA_SPACING, EXTEND_TAP_AREA_SPACING, EXTEND_TAP_AREA_SPACING, function (p) {
  return p.disabled && 'cursor: not-allowed;';
});
var sizeStyleProp = createStyleFunction([{
  styleProp: 'size',
  properties: ['fontSize'],
  getValue: function getValue(size) {
    return pxToRem(SIZE_MAP[size]);
  }
}]);
var MockBox = styled.span.withConfig({
  shouldForwardProp: function shouldForwardProp(prop) {
    return !['isChecked', 'size'].includes(prop);
  }
}).withConfig({
  displayName: "Checkbox__MockBox",
  componentId: "sc-rxonnb-2"
})(["position:relative;pointer-events:none;display:inline-flex;align-items:center;justify-content:center;width:", ";height:", ";", ";color:", ";background:", ";border:1px solid ", ";border-radius:", ";input[disabled] + &{opacity:0.5;", ";}&::before,&::after{content:'';", " opacity:0;transition:opacity 0.25s linear;pointer-events:none;}&::before{background:", ";border-radius:inherit;", ":hover &{opacity:", ";transition-duration:50ms;}}&::after{top:-3px;left:-3px;bottom:-3px;right:-3px;border-radius:inherit;box-shadow:0 0 0 3px ", ";input.focus-visible + &{opacity:1;transition-duration:50ms;}}& > svg{position:relative;transition:opacity 150ms ease-out;opacity:", ";}"], pxToEm(16), pxToEm(16), sizeStyleProp, function (p) {
  return contrast(p.theme.links);
}, function (p) {
  return p.isChecked ? p.theme.links : p.theme.background;
}, function (p) {
  return alpha(p.theme.text, p.isChecked ? 0 : 0.5);
}, pxToRem(3), function (p) {
  if (!p.isChecked) {
    var textColor = getDimmedTextColor(p.theme);
    return css(["background-image:linear-gradient( 45deg,white 25%,", " 25%,", " 50%,white 50%,white 75%,", " 75%,", " 100% );background-size:4px 4px;"], textColor, textColor, textColor, textColor);
  }
}, fillParent, function (p) {
  return p.theme.shade;
}, Wrapper, function (p) {
  return Math.min(p.theme.shadeStrength, 0.12);
}, function (p) {
  return p.theme.links;
}, function (p) {
  return p.isChecked ? 1 : 0;
});
var Checkbox = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var checked = _ref.checked,
    indeterminate = _ref.indeterminate,
    onChange = _ref.onChange,
    onClick = _ref.onClick,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'small' : _ref$size,
    tabIndex = _ref.tabIndex,
    className = _ref.className,
    style = _ref.style,
    breakpoints = _ref.breakpoints,
    otherProps = _objectWithoutProperties(_ref, _excluded);
  var inputRef = useRef(null);
  var mergedRefs = useMergedRefs([ref, inputRef]);
  var tabIndexContext = useTabIndexContext();
  useEffect(function () {
    if (inputRef.current) {
      inputRef.current.indeterminate = indeterminate || false;
    }
  }, [indeterminate]);
  var handleClick = useCallback(function (e) {
    if (onClick) onClick(e);

    /**
     * IE 11 and Edge don't fire onChange for indeterminate checkboxes,
     * therefore we're (ab)using the click handler to do so instead.
     *
     * Surprisingly, this even works with keyboard navigation
     * (i.e. when pressing Space when the input is focused).
     */
    if (indeterminate && onChange) {
      onChange(e);
    }
  }, [onClick, onChange, indeterminate]);
  var isChecked = Boolean(checked);
  return /*#__PURE__*/React.createElement(Wrapper, {
    className: className,
    style: style
  }, /*#__PURE__*/React.createElement(Input, _extends({
    type: "checkbox",
    ref: mergedRefs,
    checked: isChecked,
    onChange: indeterminate ? noop : onChange,
    onClick: handleClick,
    tabIndex: tabIndex || tabIndexContext
  }, otherProps)), /*#__PURE__*/React.createElement(MockBox, {
    "aria-hidden": "true",
    isChecked: isChecked || indeterminate || false,
    size: size,
    breakpoints: breakpoints
  }, /*#__PURE__*/React.createElement(Icon, {
    name: indeterminate ? 'minus' : 'ok',
    scale: 0.8889
  })));
});
Checkbox.displayName = 'Checkbox';
export default Checkbox;